//@flow
import React, { useCallback } from "react";
import { connect } from "react-redux";
import moment from "moment";
import { allPass } from "ramda";
import {
  type ErrosSelecao,
  type ModoApp,
  FORMATO_DATA_BANCO,
  MODO_APP_SERIES,
} from "../../../../../model";
import {
  type State,
  type ActionCreator,
  i18n,
  getDataBaseDefault,
  getDataInicialSelecionada,
  getDataFinalSelecionada,
  selecionarDataInicial,
  selecionarDataFinal,
  getIdiomaUsuario,
  getDataBaseMaxima,
  getModoApp,
} from "../../../../../state";

import { Translate } from "@quantum/i18n";

import { DateCalendarPicker } from "../../../../comum";

type Props = {
  dataInicialSelecionada: ?Date,
  dataFinalSelecionada: ?Date,
  selecionarDataInicial: ActionCreator,
  selecionarDataFinal: ActionCreator,
  dataBaseDefault: string,
  dataBaseMaxima: string,
  idiomaUsuario: string,
  modoApp: ModoApp,
};

function SelecaoDataInicialEFinal({
  dataInicialSelecionada,
  dataFinalSelecionada,
  selecionarDataInicial,
  selecionarDataFinal,
  dataBaseDefault,
  dataBaseMaxima,
  idiomaUsuario,
  modoApp,
}: Props) {
  const ehDataValida = useCallback(
    (data: moment) =>
      moment.isMoment(data) &&
      data.isValid() &&
      data.isSameOrAfter(moment("1997-01-01")) &&
      data.isSameOrBefore(
        modoApp === MODO_APP_SERIES
          ? moment(dataBaseMaxima, FORMATO_DATA_BANCO)
          : moment(new Date()).format(FORMATO_DATA_BANCO)
      ),
    [dataBaseMaxima, modoApp]
  );

  const dataFinalValida = useCallback(
    allPass([
      ehDataValida,
      data =>
        !dataInicialSelecionada ||
        moment(data).isSameOrAfter(dataInicialSelecionada, "day"),
    ]),
    [dataInicialSelecionada, ehDataValida]
  );

  return (
    <div className="ConfiguracaoMedida">
      <label className="ConfiguracaoMedida__label">
        <Translate>maiusculas.dataInicial</Translate>
      </label>
      <DateCalendarPicker
        className="ConfiguracaoMedida__input-data"
        value={dataInicialSelecionada}
        onChange={selecionarDataInicial}
        closeOnSelect={true}
        strictParsing={true}
        locale={idiomaUsuario}
        isValidDate={ehDataValida}
      />

      <label className="ConfiguracaoMedida__label">
        <Translate>maiusculas.dataFinal</Translate>
      </label>
      <DateCalendarPicker
        key={
          dataInicialSelecionada
            ? moment(dataInicialSelecionada).format("DD/MM/YYYY")
            : "none"
        }
        className="ConfiguracaoMedida__input-data"
        value={dataFinalSelecionada}
        onChange={selecionarDataFinal}
        closeOnSelect={true}
        strictParsing={true}
        locale={idiomaUsuario}
        isValidDate={dataFinalValida}
      />
    </div>
  );
}

export const validacaoSelecaoDataInicialEFinal = function(
  state: State
): ?ErrosSelecao {
  const erros: ErrosSelecao = {};

  const dataInicialSelecionada = moment(getDataInicialSelecionada(state));
  const dataFinalSelecionada = moment(getDataFinalSelecionada(state));

  const { translate } = i18n(state);

  if (!dataInicialSelecionada.isValid()) {
    erros.dataInicialInvalida = translate("erro.dataInicialInvalida");
  }
  if (!dataFinalSelecionada.isValid()) {
    erros.dataFinalInvalida = translate("erro.dataFinalInvalida");
  }

  if (dataInicialSelecionada.isBefore("1997-01-01")) {
    erros.dataInicialForaDosLimites = translate(
      "erro.dataInicialAposInicioSistema"
    );
  }

  if (dataFinalSelecionada.isAfter(moment(), "day")) {
    erros.dataFinalForaDosLimites =
      translate("erro.dataFinalAnteriorDataCorrente") ||
      "A Data final deve ser anterior à data atual";
  }

  if (dataInicialSelecionada.isAfter(dataFinalSelecionada, "day")) {
    erros.dataInicialInvalida = translate("erro.dataInicialMenorDataFinal");
  }

  return Object.keys(erros).length === 0 ? null : erros;
};

export default connect(
  (state: State) => ({
    dataInicialSelecionada: getDataInicialSelecionada(state),
    dataFinalSelecionada: getDataFinalSelecionada(state),
    dataBaseDefault: getDataBaseDefault(state),
    dataBaseMaxima: getDataBaseMaxima(state),
    idiomaUsuario: getIdiomaUsuario(state),
    modoApp: getModoApp(state),
  }),
  { selecionarDataInicial, selecionarDataFinal }
)(SelecaoDataInicialEFinal);
